import { useState, useEffect, useRef } from 'react';
import { Outlet, useLoaderData, useLocation, Navigate } from 'react-router-dom';

import { isMerchant } from '../../lib/guard.ts';
import { Navbar } from '../../components/Navbar.jsx';
import { Sidebar } from '../../components/Sidebar.jsx';
import { NotificationToastContainer } from '../../components/NotificationToast.jsx';
import { SubscriptionBlocker } from '../../componentes/SubscriptionBlocker.jsx';
import { StoreOnboarding } from '../../componentes/StoreOnboarding.jsx';
import { BrowserNotificationPrompt } from '../../componentes/BrowserNotificationPrompt.jsx';
import { InstallPrompt } from '../../componentes/InstallPrompt.jsx';
import { useNotifications } from '../../features/notificaciones/useNotifications.ts';
import { playNotificationSound } from '../../lib/notificationSound.ts';
import { useBrowserNotifications } from '../../hooks/useBrowserNotifications.js';
import { showNewOrderNotification, showGenericNotification } from '../../lib/browserNotifications.js';

const LAST_NOTIFICATION_KEY = 'lastNotificationId';

export default function PanelLayout() {
  // 🛡️ Verificación síncrona: debe ser merchant
  if (!isMerchant()) {
    return <Navigate to="/inicio-sesion" replace />;
  }

  const subscriptionStatus = useLoaderData();
  const location = useLocation();
  const { data: notifications = [] } = useNotifications();
  const [toastNotifications, setToastNotifications] = useState([]);
  const { isGranted: browserNotificationsEnabled } = useBrowserNotifications();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  
  // Ref para evitar duplicados
  const processedNotifications = useRef(new Set());
  
  // Recuperar el último ID de notificación desde localStorage al montar
  const [lastNotificationId, setLastNotificationId] = useState(() => {
    const saved = localStorage.getItem(LAST_NOTIFICATION_KEY);
    const id = saved ? parseInt(saved, 10) : null;
    // Agregar al Set de procesados si existe
    if (id) processedNotifications.current.add(id);
    return id;
  });

  // Detectar nuevas notificaciones y mostrar toast + notificación del navegador
  useEffect(() => {
    if (notifications.length === 0) return;

    const latestNotification = notifications[0];
    
    // Si hay una nueva notificación no leída que no hemos procesado
    if (
      !latestNotification.is_read &&
      latestNotification.id !== lastNotificationId &&
      !processedNotifications.current.has(latestNotification.id)
    ) {
      // Marcar como procesada INMEDIATAMENTE para evitar duplicados
      processedNotifications.current.add(latestNotification.id);
      
      // Guardar el nuevo ID tanto en estado como en localStorage
      setLastNotificationId(latestNotification.id);
      localStorage.setItem(LAST_NOTIFICATION_KEY, latestNotification.id.toString());
      
      // Reproducir sonido de notificación
      playNotificationSound();
      
      // Agregar al array de toasts (notificación in-app)
      setToastNotifications((prev) => [...prev, latestNotification]);
      
      // Mostrar notificación del navegador si está habilitado
      if (browserNotificationsEnabled) {
        // Si es un pedido nuevo, usar notificación especial
        if (latestNotification.type === 'order' && latestNotification.data?.order) {
          showNewOrderNotification(latestNotification.data.order);
        } else {
          // Notificación genérica
          showGenericNotification(latestNotification);
        }
      }
    }
  }, [notifications, lastNotificationId, browserNotificationsEnabled]);

  const removeToast = (id) => {
    setToastNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-slate-200 font-sans selection:bg-white/20">
      {/* Navbar Sticky Superior */}
      <Navbar 
        isFeedbackOpen={isFeedbackOpen}
        setIsFeedbackOpen={setIsFeedbackOpen}
      />

      {/* Contenedor Relativo para posicionar elementos fijos */}
      <div className="relative">
        
        {/* Sidebar Fijo (Fixed) */}
        <Sidebar onOpenFeedback={() => setIsFeedbackOpen(true)} />

        {/* 
           CONTENIDO PRINCIPAL 
           lg:pl-64 -> Aquí ocurre la magia. Empujamos el contenido 256px (w-64) a la derecha 
           para dejarle el hueco al Sidebar fijo.
        */}
        <main className="w-full lg:pl-64 transition-all duration-300">
          {/* Contenedor interno para centrar el contenido y darle aire */}
          <div 
            key={location.pathname}
            className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8 animate-fadeIn"
          >
            <Outlet />
          </div>
        </main>

      </div>
      
      {/* --- MODALES Y TOASTS --- */}
      
      {/* Onboarding para nuevos usuarios */}
      <StoreOnboarding />
      
      {/* Contenedor de notificaciones Toast */}
      <NotificationToastContainer
        notifications={toastNotifications}
        onRemove={removeToast}
      />
      
      {/* Prompt para activar notificaciones del navegador */}
      <BrowserNotificationPrompt />
      
      {/* Prompt para instalar PWA */}
      <InstallPrompt />
      
      {/* Blocker de suscripción - ÚNICO modal para manejar estados de suscripción */}
      <SubscriptionBlocker subscriptionStatus={subscriptionStatus} />
    </div>
  );
}
